import { useState } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    id: 1,
    icon: 'ri-t-shirt-line',
    title: 'T-Shirt & Apparel Printing',
    teaser: 'Custom apparel for teams, events, churches & businesses',
    description: 'High-quality custom apparel printing that makes your brand wearable. From single pieces to bulk orders, we handle everything with vibrant, long-lasting results.',
    benefits: [
      'Screen printing & heat transfer',
      'Bulk and single-item orders',
      'Churches, teams & corporate apparel',
      'Full-color custom artwork & designs'
    ],
    image: 'https://public.readdy.ai/ai/img_res/68edef8d399499ea30c6c364b65e5b61.jpg'
  },
  {
    id: 2,
    icon: 'ri-palette-line',
    title: 'Branding & Logo Design',
    teaser: 'A brand identity that makes you instantly recognizable',
    description: 'We craft compelling brand identities that tell your story, build trust, and leave a lasting impression on every customer you meet.',
    benefits: [
      'Custom logo design & variations',
      'Brand color palette & typography',
      'Brand guidelines document',
      'Full identity package delivery'
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20professional%20branding%20and%20logo%20design%20process%20with%20sketches%20and%20final%20logo%20mockups%20on%20clean%20white%20desk%2C%20brand%20identity%20presentation%20with%20color%20swatches%20and%20typography%20specimens%2C%20elegant%20minimalist%20creative%20studio%20workspace&width=800&height=600&seq=svc-branding&orientation=landscape'
  },
  {
    id: 3,
    icon: 'ri-pencil-ruler-2-line',
    title: 'Graphic Design',
    teaser: 'Creative visuals that communicate your message powerfully',
    description: 'From flyers to full marketing campaigns, our graphic design services deliver sharp, purposeful visuals that capture attention and drive action.',
    benefits: [
      'Flyers, brochures & posters',
      'Social media graphics & ads',
      'Church bulletins & event programs',
      'Presentations & infographics'
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20graphic%20design%20studio%20with%20vibrant%20marketing%20flyers%20brochures%20and%20print%20materials%20arranged%20on%20a%20bright%20clean%20workspace%2C%20colorful%20design%20mockups%20spread%20on%20modern%20white%20desk%20with%20creative%20tools%2C%20inspiring%20professional%20creative%20environment&width=800&height=600&seq=svc-graphic&orientation=landscape'
  },
  {
    id: 4,
    icon: 'ri-store-2-line',
    title: 'Signage & Print',
    teaser: 'Signage that commands attention and drives foot traffic',
    description: 'From storefront signs to indoor displays, we design and produce premium signage that puts your business on the map — literally.',
    benefits: [
      'Indoor & outdoor signage',
      'Window graphics & decals',
      'Foam board & PVC signs',
      'Installation-ready production'
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20storefront%20business%20signage%20with%20bold%20modern%20lettering%20on%20clean%20building%20facade%2C%20high%20quality%20outdoor%20sign%20installation%20with%20vibrant%20colors%20and%20crisp%20typography%2C%20polished%20commercial%20exterior%20sign%20on%20bright%20contemporary%20retail%20building&width=800&height=600&seq=svc-signage&orientation=landscape'
  },
  {
    id: 5,
    icon: 'ri-flag-2-line',
    title: 'Banners & Displays',
    teaser: 'Bold banners and displays built to stop people in their tracks',
    description: 'Make a statement at events, trade shows, churches, and outdoor spaces with large-format banners and professional display systems that stand out.',
    benefits: [
      'Vinyl & mesh outdoor banners',
      'Retractable pull-up stands',
      'Trade show booth displays',
      'Event & church banners'
    ],
    image: 'https://readdy.ai/api/search-image?query=large%20format%20vinyl%20banner%20printing%20and%20professional%20trade%20show%20display%20stands%20in%20a%20bright%20exhibition%20hall%2C%20colorful%20event%20banners%20and%20retractable%20pull-up%20displays%20with%20bold%20graphics%2C%20clean%20professional%20marketing%20display%20setup%20at%20a%20business%20event&width=800&height=600&seq=svc-banners&orientation=landscape'
  },
  {
    id: 6,
    icon: 'ri-contacts-book-2-line',
    title: 'Business Cards & Stationery',
    teaser: 'First impressions start with the right card in hand',
    description: 'We design and print professional business cards, letterheads, envelopes, and stationery that reflect the quality of your brand every single time.',
    benefits: [
      'Premium business card design & print',
      'Letterheads & envelopes',
      'Notepads & branded stationery',
      'Matte, gloss & specialty finishes'
    ],
    image: 'https://readdy.ai/api/search-image?query=elegant%20premium%20business%20cards%20and%20branded%20stationery%20set%20including%20letterhead%20and%20envelopes%20arranged%20on%20clean%20white%20marble%20surface%2C%20sophisticated%20professional%20print%20design%20with%20modern%20minimalist%20layout%20and%20premium%20paper%20textures&width=800&height=600&seq=svc-cards&orientation=landscape'
  }
];

export default function ServicesSection() {
  const [expandedCard, setExpandedCard] = useState(1);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#06B6D4] text-sm font-semibold uppercase tracking-wider">
            What We Do
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Services Designed To MAGNIFY Your Brand
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            From apparel to signage, we deliver comprehensive creative and print services that help your business, church, or organization stand out and make an impact.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              className={`relative rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer group ${
                expandedCard === service.id ? 'md:col-span-3' : ''
              }`}
              onClick={() => setExpandedCard(service.id)}
            >
              {expandedCard === service.id ? (
                <div className="relative h-96">
                  <div className="w-full h-full">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
                    <div className="max-w-4xl">
                      <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                        {service.title}
                      </h3>
                      <p className="text-lg text-white/90 mb-6">
                        {service.description}
                      </p>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                        {service.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-2 text-white">
                            <i className="ri-checkbox-circle-fill text-[#06B6D4] text-lg mt-0.5 flex-shrink-0"></i>
                            <span className="text-base">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/services"
                        className="inline-flex items-center text-[#06B6D4] font-semibold hover:text-[#0F9A8A] transition-colors group/link"
                      >
                        Learn More
                        <i className="ri-arrow-right-line ml-2 group-hover/link:translate-x-1 transition-transform"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-8 h-64 flex flex-col items-center justify-center text-center hover:bg-gray-100 transition-colors">
                  <div className="w-16 h-16 bg-[#06B6D4]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-[#06B6D4] text-3xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {service.teaser}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
